import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "./AppShell";
import { useAuth } from "@/lib/auth";
import QuotaInline from "@/components/QuotaInline";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function NewAudit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState(user?.website_url || "");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setConfirmOpen(true);
  };

  const runAudit = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const res = await api.post("/audits", { website_url: url });
      toast.success("Audit started. Running crawl + scoring…");
      navigate(`/audits/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start audit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Run a new audit" subtitle="// new audit">
      <div className="max-w-3xl" data-testid="new-audit-page">
        <QuotaInline resource="audits_monthly" period="month" />
        <p className="text-lg text-muted-foreground max-w-2xl">
          Paste a URL. We'll crawl it, score it across 9 categories, and write business-friendly explanations + prioritised recommendations.
        </p>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="audit-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Run an AI Visibility audit?</AlertDialogTitle>
              <AlertDialogDescription>
                We'll crawl <span className="font-mono text-foreground break-all">{url}</span> and generate AI explanations + fixes.
                Each audit uses part of your monthly quota — make sure the URL is correct.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="audit-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={runAudit} data-testid="audit-confirm-run">Yes, run audit</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <form onSubmit={onSubmit} className="mt-10 flex flex-col sm:flex-row gap-2" data-testid="new-audit-form">
          <input
            type="text"
            required
            value={url}
            data-testid="new-audit-url-input"
            placeholder="https://yourbusiness.com"
            onChange={(e) => setUrl(e.target.value)}
            className="flex-1 bg-card border border-border px-4 py-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="new-audit-submit-button"
            className="px-6 py-4 bg-obsidian text-bone font-app font-bold uppercase tracking-wider text-sm hover:bg-intl transition-colors disabled:opacity-60"
          >
            {busy ? "Starting…" : "Run audit"}
          </button>
        </form>

        <div className="mt-10 grid md:grid-cols-3 gap-4 text-sm text-muted-foreground">
          <div className="border border-border p-5 bg-card">
            <div className="font-mono text-xs uppercase tracking-widest">// step 1</div>
            <div className="font-app text-base mt-1 text-foreground">Crawl</div>
            <div className="mt-2">We fetch your homepage and extract schema, FAQ, semantic markup, headings, links, sitemap, robots and speed.</div>
          </div>
          <div className="border border-border p-5 bg-card">
            <div className="font-mono text-xs uppercase tracking-widest">// step 2</div>
            <div className="font-app text-base mt-1 text-foreground">Score</div>
            <div className="mt-2">Deterministic scoring across 9 weighted categories — total 0-100.</div>
          </div>
          <div className="border border-border p-5 bg-card">
            <div className="font-mono text-xs uppercase tracking-widest">// step 3</div>
            <div className="font-app text-base mt-1 text-foreground">Explain & fix</div>
            <div className="mt-2">AI translates findings into plain English and queues actionable fixes.</div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
